import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
} from 'react';

import { cn } from '../base/utils';
import {
  WorkbenchGallerySettingsButton,
  type WorkbenchGallerySettingsButtonProps,
} from './WorkbenchGallerySettings';

export interface WorkbenchContentToolbarViewModeOption {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface WorkbenchContentToolbarClassNames {
  root?: string;
  start?: string;
  end?: string;
  viewModeRoot?: string;
  viewModeTrigger?: string;
  viewModeMenu?: string;
  viewModeItem?: string;
  settings?: string;
}

export interface WorkbenchContentToolbarProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'className'> {
  as?: ElementType;
  children?: ReactNode;
  end?: ReactNode;
  viewMode?: string;
  viewModeOptions?: WorkbenchContentToolbarViewModeOption[];
  onViewModeChange?: (nextValue: string, option: WorkbenchContentToolbarViewModeOption) => void;
  gallerySettings?: WorkbenchGallerySettingsButtonProps | false;
  gallerySettingsPlacement?: 'start' | 'end';
  className?: string;
  startClassName?: string;
  endClassName?: string;
  classNames?: WorkbenchContentToolbarClassNames;
  [key: string]: any;
}

function TableViewIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M9 4v16" />
    </svg>
  );
}

function GalleryViewIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 7h-9M7 7H4M14 17H4M20 17h-3" />
      <circle cx="9" cy="7" r="2" />
      <circle cx="16" cy="17" r="2" />
    </svg>
  );
}

function ChevronDownIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={cn('h-3 w-3 opacity-55 transition-transform', open && 'rotate-180')}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

const defaultViewModeOptions: WorkbenchContentToolbarViewModeOption[] = [
  { value: 'list', label: 'Table view', icon: <TableViewIcon /> },
  { value: 'gallery', label: 'Gallery view', icon: <GalleryViewIcon /> },
];

function getViewModeOptionIcon(option: WorkbenchContentToolbarViewModeOption) {
  if (option.icon !== undefined) return option.icon;
  if (option.value === 'list' || option.value === 'table') return <TableViewIcon />;
  if (option.value === 'gallery') return <GalleryViewIcon />;
  return null;
}

function getSettingsTriggerLabel(settings: WorkbenchGallerySettingsButtonProps) {
  return settings.triggerLabel ?? settings.labels?.settingsButton ?? 'Settings';
}

function WorkbenchContentToolbarViewModeControl({
  value,
  options,
  onValueChange,
  classNames,
}: {
  value?: string;
  options: WorkbenchContentToolbarViewModeOption[];
  onValueChange?: (nextValue: string, option: WorkbenchContentToolbarViewModeOption) => void;
  classNames?: WorkbenchContentToolbarClassNames;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const activeOption = options.find((option) => option.value === value) || options.find((option) => !option.disabled) || options[0];
  const activeIcon = activeOption ? getViewModeOptionIcon(activeOption) : null;

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (!activeOption) return null;

  return (
    <div ref={rootRef} className={cn('relative inline-flex', classNames?.viewModeRoot)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'inline-flex h-7 min-w-0 items-center justify-center gap-1.5 rounded-md border border-border/70 bg-card px-2 text-xs font-medium text-foreground shadow-none transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25',
          classNames?.viewModeTrigger,
        )}
        onClick={() => setOpen((current) => !current)}
      >
        {activeIcon ? (
          <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center text-muted-foreground">
            {activeIcon}
          </span>
        ) : null}
        <span className="min-w-0 truncate">{activeOption.label}</span>
        <ChevronDownIcon open={open} />
      </button>

      {open ? (
        <div
          role="listbox"
          className={cn(
            'absolute left-0 top-full z-50 mt-2 min-w-[160px] overflow-hidden rounded-xl border border-border/60 bg-card/95 p-1.5 text-xs text-foreground shadow-lg backdrop-blur-xl',
            classNames?.viewModeMenu,
          )}
        >
          {options.map((option) => {
            const selected = option.value === activeOption.value;
            const optionIcon = getViewModeOptionIcon(option);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={option.disabled}
                className={cn(
                  'flex min-h-8 w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/55 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50',
                  selected && 'bg-muted/60 text-foreground',
                  classNames?.viewModeItem,
                )}
                onClick={() => {
                  if (option.disabled) return;
                  onValueChange?.(option.value, option);
                  setOpen(false);
                }}
              >
                {optionIcon ? (
                  <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    {optionIcon}
                  </span>
                ) : null}
                <span className="min-w-0 truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export const WorkbenchContentToolbar = forwardRef<HTMLElement, WorkbenchContentToolbarProps>(
  function WorkbenchContentToolbar(
    {
      as: Component = 'div',
      children,
      end,
      viewMode,
      viewModeOptions,
      onViewModeChange,
      gallerySettings,
      gallerySettingsPlacement = 'start',
      className,
      startClassName,
      endClassName,
      classNames,
      ...props
    },
    ref,
  ) {
    const resolvedViewModeOptions = viewModeOptions || (viewMode ? defaultViewModeOptions : []);
    const resolvedGallerySettings = gallerySettings && typeof gallerySettings === 'object' ? gallerySettings : null;
    const shouldRenderViewMode = Boolean(resolvedViewModeOptions.length);
    const shouldRenderSettings = Boolean(resolvedGallerySettings);
    const shouldRenderSettingsInStart = shouldRenderSettings && gallerySettingsPlacement !== 'end';
    const shouldRenderSettingsInEnd = shouldRenderSettings && gallerySettingsPlacement === 'end';
    const shouldRenderStart = shouldRenderViewMode || shouldRenderSettingsInStart || Boolean(children);
    const shouldRenderEnd = Boolean(end) || shouldRenderSettingsInEnd;

    return (
      <Component
        ref={ref}
        data-workbench-content-toolbar
        className={cn(
          'flex min-h-[46px] shrink-0 flex-col gap-2 border-b border-border/60 px-4 py-2 md:flex-row md:items-center md:justify-between',
          classNames?.root,
          className,
        )}
        {...props}
      >
        {shouldRenderStart ? (
          <div className={cn('flex flex-wrap items-center gap-2', classNames?.start, startClassName)}>
            {shouldRenderViewMode ? (
              <WorkbenchContentToolbarViewModeControl
                value={viewMode}
                options={resolvedViewModeOptions}
                onValueChange={onViewModeChange}
                classNames={classNames}
              />
            ) : null}
            {shouldRenderSettingsInStart && resolvedGallerySettings ? (
              <WorkbenchGallerySettingsButton
                align="end"
                triggerIcon={<SettingsIcon />}
                triggerLabel={getSettingsTriggerLabel(resolvedGallerySettings)}
                {...resolvedGallerySettings}
                buttonClassName={cn(
                  '!h-7 !w-7 !min-h-0 !rounded-md !p-0 text-xs',
                  classNames?.settings,
                  resolvedGallerySettings.buttonClassName,
                )}
              />
            ) : null}
            {children}
          </div>
        ) : null}
        {shouldRenderEnd ? (
          <div className={cn('flex shrink-0 items-center gap-2', classNames?.end, endClassName)}>
            {end}
            {shouldRenderSettingsInEnd && resolvedGallerySettings ? (
              <WorkbenchGallerySettingsButton
                align="end"
                triggerIcon={<SettingsIcon />}
                triggerLabel={getSettingsTriggerLabel(resolvedGallerySettings)}
                {...resolvedGallerySettings}
                buttonClassName={cn(
                  '!h-7 !w-7 !min-h-0 !rounded-md !p-0 text-xs',
                  classNames?.settings,
                  resolvedGallerySettings.buttonClassName,
                )}
              />
            ) : null}
          </div>
        ) : null}
      </Component>
    );
  },
);
